import * as S from './styles';
import PanelOptionMixin from '../mixin';
import ChartColumnIcon from '@svgIcons/dashboard/widget_list_icons/column.svg';
import Switch from '@components/Common/Switch';
import withHMR from '@/utils/withHMR';
import { CHART_TYPES } from '@/utils/consts';

const MAX_VALUE = 1_000_000_000_000_000;

const { COMBO_CHART } = CHART_TYPES;

const ComboAxesConfigOption = {
    name: 'ComboAxesConfigOption',
    mixins: [PanelOptionMixin],
    props: {
        axesConfig: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            primaryMinValue: null,
            primaryMaxValue: null,
            secondaryMinValue: null,
            secondaryMaxValue: null,
            localPrimaryAutoScale: null,
            localSecondaryAutoScale: null,
        };
    },
    computed: {
        showOption: R.compose(
            R.includes(R.__, [COMBO_CHART]),
            R.pathOr('', ['graphOptions', 'selectedGraphType'])
        ),
        isHorizontal() {
            return false;
        },
        primaryAxisKey() {
            return this.isHorizontal ? 'topXAxis' : 'leftYAxis';
        },
        secondaryAxisKey() {
            return this.isHorizontal ? 'bottomXAxis' : 'rightYAxis';
        },
        primaryAxisConfig() {
            return this.axesConfig?.[this.primaryAxisKey] ?? {};
        },
        secondaryAxisConfig() {
            return this.axesConfig?.[this.secondaryAxisKey] ?? {};
        },
        primaryAutoScaleEnabled() {
            if (this.localPrimaryAutoScale !== null) {
                return this.localPrimaryAutoScale;
            }
            return this.primaryAxisConfig?.autoScaleEnabled ?? true;
        },
        secondaryAutoScaleEnabled() {
            if (this.localSecondaryAutoScale !== null) {
                return this.localSecondaryAutoScale;
            }
            return this.secondaryAxisConfig?.autoScaleEnabled ?? true;
        },
        hasPrimaryMinMaxError() {
            const { min, max } = this.primaryAxisConfig;
            if (!min || !max) return false;
            return Number(min) > Number(max);
        },
        hasSecondaryMinMaxError() {
            const { min, max } = this.secondaryAxisConfig;
            if (!min || !max) return false;
            return Number(min) > Number(max);
        },
        hasMinMaxError() {
            return this.hasPrimaryMinMaxError || this.hasSecondaryMinMaxError;
        },
    },
    watch: {
        primaryMinValue(newValue) {
            this.$emit('update', {
                [this.primaryAxisKey]: {
                    min: newValue,
                },
            });
        },
        primaryMaxValue(newValue) {
            this.$emit('update', {
                [this.primaryAxisKey]: {
                    max: newValue,
                },
            });
        },
        secondaryMinValue(newValue) {
            this.$emit('update', {
                [this.secondaryAxisKey]: {
                    min: newValue,
                },
            });
        },
        secondaryMaxValue(newValue) {
            this.$emit('update', {
                [this.secondaryAxisKey]: {
                    max: newValue,
                },
            });
        },
        'primaryAxisConfig.autoScaleEnabled'(newValue) {
            this.localPrimaryAutoScale = newValue ?? true;
        },
        'secondaryAxisConfig.autoScaleEnabled'(newValue) {
            this.localSecondaryAutoScale = newValue ?? true;
        },
    },
    methods: {
        handleAutoScalingToggle({ isSecondary }) {
            const axisKey = isSecondary
                ? this.secondaryAxisKey
                : this.primaryAxisKey;
            const currentValue = isSecondary
                ? this.secondaryAutoScaleEnabled
                : this.primaryAutoScaleEnabled;

            const newValue = !currentValue;

            if (isSecondary) {
                this.localSecondaryAutoScale = newValue;
            } else {
                this.localPrimaryAutoScale = newValue;
            }

            this.$emit('update', {
                [axisKey]: {
                    autoScaleEnabled: newValue,
                },
            });
        },

        checkIsValidInput(value) {
            if (!/^-?\d*\.?\d*$/.test(value)) return false;

            const numValue = Number(value);
            return (
                !isNaN(numValue) &&
                numValue >= -MAX_VALUE &&
                numValue <= MAX_VALUE
            );
        },

        handleValueChange(e, type) {
            const value = e.target.value;

            if (value === '') {
                switch (type) {
                    case 'primaryMin':
                        this.primaryMinValue = null;
                        break;
                    case 'primaryMax':
                        this.primaryMaxValue = null;
                        break;
                    case 'secondaryMin':
                        this.secondaryMinValue = null;
                        break;
                    case 'secondaryMax':
                        this.secondaryMaxValue = null;
                        break;
                }
                return;
            }

            if (value === '-') {
                return;
            }

            if (!this.checkIsValidInput(value)) {
                switch (type) {
                    case 'primaryMin':
                        e.target.value = this.primaryMinValue;
                        break;
                    case 'primaryMax':
                        e.target.value = this.primaryMaxValue;
                        break;
                    case 'secondaryMin':
                        e.target.value = this.secondaryMinValue;
                        break;
                    case 'secondaryMax':
                        e.target.value = this.secondaryMaxValue;
                        break;
                }
                return;
            }

            const valueToSet = Number(value);

            switch (type) {
                case 'primaryMin':
                    this.primaryMinValue = valueToSet;
                    break;
                case 'primaryMax':
                    this.primaryMaxValue = valueToSet;
                    break;
                case 'secondaryMin':
                    this.secondaryMinValue = valueToSet;
                    break;
                case 'secondaryMax':
                    this.secondaryMaxValue = valueToSet;
                    break;
            }
        },

        handleReset() {
            this.primaryMinValue = null;
            this.primaryMaxValue = null;
            this.secondaryMinValue = null;
            this.secondaryMaxValue = null;
            this.localPrimaryAutoScale = true;
            this.localSecondaryAutoScale = true;

            this.$emit('update', {
                [this.primaryAxisKey]: {
                    autoScaleEnabled: true,
                    min: null,
                    max: null,
                },
                [this.secondaryAxisKey]: {
                    autoScaleEnabled: true,
                    min: null,
                    max: null,
                },
            });
        },
    },
    created() {
        this.primaryMinValue = this.primaryAxisConfig?.min ?? null;
        this.primaryMaxValue = this.primaryAxisConfig?.max ?? null;
        this.secondaryMinValue = this.secondaryAxisConfig?.min ?? null;
        this.secondaryMaxValue = this.secondaryAxisConfig?.max ?? null;
        this.localPrimaryAutoScale =
            this.primaryAxisConfig?.autoScaleEnabled ?? true;
        this.localSecondaryAutoScale =
            this.secondaryAxisConfig?.autoScaleEnabled ?? true;
    },
    render() {
        if (!this.showOption) {
            return null;
        }

        return (
            <S.ComponentWrapper>
                <S.Header>
                    <S.Title>
                        <ChartColumnIcon width={16} height={16} v-view-box />
                        <h1>
                            {this.$t('NEW_DASHBOARD.axes_config_option_title', {
                                axis: this.isHorizontal ? 'X' : 'Y',
                            })}
                        </h1>
                    </S.Title>
                    <S.ResetButton onClick={this.handleReset}>
                        {this.$t('COMMON.reset')}
                    </S.ResetButton>
                </S.Header>
                <S.Content>
                    <S.Row>
                        <S.RowTitle>
                            {this.$t(
                                'NEW_DASHBOARD.axes_config_option_auto_scaling',
                                {
                                    axis: this.isHorizontal ? 'X' : 'Y',
                                }
                            )}
                        </S.RowTitle>
                        <S.SwitchesWrapper>
                            <S.SwitchGroup>
                                <S.AxisLabel>
                                    {this.isHorizontal
                                        ? this.$t('COMMON.top')
                                        : this.$t('COMMON.left')}
                                </S.AxisLabel>
                                <Switch
                                    value={this.primaryAutoScaleEnabled}
                                    type="big"
                                    onClick={() =>
                                        this.handleAutoScalingToggle({
                                            isSecondary: false,
                                        })
                                    }
                                />
                            </S.SwitchGroup>
                            <S.Divider />
                            <S.SwitchGroup>
                                <S.AxisLabel>
                                    {this.isHorizontal
                                        ? this.$t('COMMON.bottom')
                                        : this.$t('COMMON.right')}
                                </S.AxisLabel>
                                <Switch
                                    value={this.secondaryAutoScaleEnabled}
                                    type="big"
                                    onClick={() =>
                                        this.handleAutoScalingToggle({
                                            isSecondary: true,
                                        })
                                    }
                                />
                            </S.SwitchGroup>
                        </S.SwitchesWrapper>
                    </S.Row>

                    {!this.primaryAutoScaleEnabled && (
                        <S.Row style={{ alignItems: 'flex-start' }}>
                            <S.RowTitle>
                                {this.isHorizontal
                                    ? this.$t('COMMON.top')
                                    : this.$t('COMMON.left')}
                            </S.RowTitle>
                            <S.AxisControls>
                                <S.InputGroup>
                                    <S.AxisLabel>
                                        {this.$t(
                                            'NEW_DASHBOARD.axes_config_option_min_value'
                                        )}
                                    </S.AxisLabel>
                                    <S.InputWrapper>
                                        <input
                                            type="text"
                                            placeholder={this.$t(
                                                'NEW_DASHBOARD.axes_config_option_min_value_placeholder'
                                            )}
                                            value={this.primaryMinValue}
                                            disabled={
                                                this.primaryAutoScaleEnabled
                                            }
                                            onInput={(e) =>
                                                this.handleValueChange(
                                                    e,
                                                    'primaryMin'
                                                )
                                            }
                                        />
                                    </S.InputWrapper>
                                </S.InputGroup>
                                <S.InputGroup>
                                    <S.AxisLabel>
                                        {this.$t(
                                            'NEW_DASHBOARD.axes_config_option_max_value'
                                        )}
                                    </S.AxisLabel>
                                    <S.InputWrapper>
                                        <input
                                            type="text"
                                            placeholder={this.$t(
                                                'NEW_DASHBOARD.axes_config_option_max_value_placeholder'
                                            )}
                                            value={this.primaryMaxValue}
                                            disabled={
                                                this.primaryAutoScaleEnabled
                                            }
                                            onInput={(e) =>
                                                this.handleValueChange(
                                                    e,
                                                    'primaryMax'
                                                )
                                            }
                                        />
                                    </S.InputWrapper>
                                </S.InputGroup>
                                {this.hasPrimaryMinMaxError && (
                                    <S.ErrorText
                                        style={{
                                            textAlign: 'right',
                                            width: '100%',
                                        }}
                                    >
                                        {this.$t(
                                            'NEW_DASHBOARD.axes_config_option_min_max_error'
                                        )}
                                    </S.ErrorText>
                                )}
                            </S.AxisControls>
                        </S.Row>
                    )}
                    {!this.secondaryAutoScaleEnabled && (
                        <S.Row
                            style={{
                                alignItems: 'flex-start',
                            }}
                        >
                            <S.RowTitle>
                                {this.isHorizontal
                                    ? this.$t('COMMON.bottom')
                                    : this.$t('COMMON.right')}
                            </S.RowTitle>
                            <S.AxisControls>
                                <S.InputGroup>
                                    <S.AxisLabel>
                                        {this.$t(
                                            'NEW_DASHBOARD.axes_config_option_min_value'
                                        )}
                                    </S.AxisLabel>
                                    <S.InputWrapper>
                                        <input
                                            type="text"
                                            placeholder={this.$t(
                                                'NEW_DASHBOARD.axes_config_option_min_value_placeholder'
                                            )}
                                            value={this.secondaryMinValue}
                                            disabled={
                                                this.secondaryAutoScaleEnabled
                                            }
                                            onInput={(e) =>
                                                this.handleValueChange(
                                                    e,
                                                    'secondaryMin'
                                                )
                                            }
                                        />
                                    </S.InputWrapper>
                                </S.InputGroup>
                                <S.InputGroup>
                                    <S.AxisLabel>
                                        {this.$t(
                                            'NEW_DASHBOARD.axes_config_option_max_value'
                                        )}
                                    </S.AxisLabel>
                                    <S.InputWrapper>
                                        <input
                                            type="text"
                                            placeholder={this.$t(
                                                'NEW_DASHBOARD.axes_config_option_max_value_placeholder'
                                            )}
                                            value={this.secondaryMaxValue}
                                            disabled={
                                                this.secondaryAutoScaleEnabled
                                            }
                                            onInput={(e) =>
                                                this.handleValueChange(
                                                    e,
                                                    'secondaryMax'
                                                )
                                            }
                                        />
                                    </S.InputWrapper>
                                </S.InputGroup>
                                {this.hasSecondaryMinMaxError && (
                                    <S.ErrorText
                                        style={{
                                            textAlign: 'right',
                                            width: '100%',
                                        }}
                                    >
                                        {this.$t(
                                            'NEW_DASHBOARD.axes_config_option_min_max_error'
                                        )}
                                    </S.ErrorText>
                                )}
                            </S.AxisControls>
                        </S.Row>
                    )}
                </S.Content>
            </S.ComponentWrapper>
        );
    },
};

export default withHMR(
    module,
    ComboAxesConfigOption,
    ComboAxesConfigOption.name
);
